/**
 * OnboardingForm — public application/onboarding form for prospective hires.
 *
 *   • Public route (no auth).  Mounted at /onboarding.
 *   • Locations come from the public locations endpoint and may include
 *     lat/lng metadata for the inline map preview.
 *   • Submits to `/api/public/onboarding/submit`.
 *
 * This file is a thin orchestrator — the heavy lifting (sections, primitives,
 * identity-document upload, completion ring, autosave, etc.) lives in
 * `./onboarding/*`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  User, EnvelopeSimple, Storefront, CheckCircle,
} from '@phosphor-icons/react';
import { OnboardingPublicAPI } from '../api/onboardingApi';
import { useTheme } from '../hooks/useTheme';
import ConnectionSourceLogo from '../components/ConnectionSourceLogo';
import {
  FloatingShapes, ProgressIndicator, SectionHero, NavigationBar, DoneScreen,
} from './onboarding/primitives';
import { PersonalSection } from './onboarding/PersonalSection';
import { ContactSection } from './onboarding/ContactSection';
import { WorkSection } from './onboarding/WorkSection';
import { ReviewSection } from './onboarding/ReviewSection';
import { WelcomeIntro } from './onboarding/WelcomeIntro';
import { CursorGlow } from './onboarding/CursorGlow';
import { CompletionRing } from './onboarding/CompletionRing';
import { AutosaveChip, loadDraft, clearDraft } from './onboarding/AutosaveChip';

const SECTIONS = [
  {
    id: 'personal',
    title: 'About you',
    subtitle: 'The basics — just so we know who we\'re talking to.',
    icon: User,
    gradient: ['#CD040B', '#F59E0B'],
  },
  {
    id: 'contact',
    title: 'How can we reach you?',
    subtitle: 'Email and phone for next-step coordination.',
    icon: EnvelopeSimple,
    gradient: ['#F59E0B', '#F5D64D'],
  },
  {
    id: 'work',
    title: 'Your perfect role',
    subtitle: 'Pick a store, tell us when you can start, and what you\'re after.',
    icon: Storefront,
    gradient: ['#10B981', '#06B6D4'],
  },
  {
    id: 'review',
    title: 'Review & send',
    subtitle: 'Take one more look — then we\'re off to the races.',
    icon: CheckCircle,
    gradient: ['#A855F7', '#F5D64D'],
  },
];

const initialForm = {
  full_name: '',
  preferred_name: '',
  email: '',
  phone: '',
  date_of_birth: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip_code: '',
  shirt_size: '',
  ssn_encrypted: '',
  ssn_last4: '',
  work_location: '',
  position: '',
  earliest_start_date: '',
  employment_type: '',
  can_pass_background_check: null,
  consent_contact: true,
  attachment_ids: [],
};

/**
 * Rough fill-percent across the fields the applicant is expected to touch.
 * Drives the gold completion ring next to the logo.
 */
function calcCompletion(form) {
  const checks = [
    !!form.full_name?.trim(),
    !!form.date_of_birth,
    !!form.shirt_size,
    !!form.address_line1,
    !!form.city,
    !!form.state,
    !!form.zip_code,
    !!form.ssn_encrypted,
    (form.attachment_ids || []).length >= 1,
    !!form.email && /^\S+@\S+\.\S+$/.test(form.email),
    !!form.phone && form.phone.replace(/\D/g, '').length >= 7,
    !!form.work_location,
    !!form.position,
    !!form.employment_type,
    !!form.earliest_start_date,
    form.can_pass_background_check != null,
    form.consent_contact === true,
  ];
  const filled = checks.filter(Boolean).length;
  return Math.round((filled / checks.length) * 100);
}

export default function OnboardingForm() {
  const theme = useTheme();
  const isLight = theme === 'light';
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => {
    const draft = loadDraft();
    return draft ? { ...initialForm, ...draft, attachment_ids: [] } : initialForm;
  });
  const [locations, setLocations] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    OnboardingPublicAPI.locations()
      .then(r => setLocations(r.locations || []))
      .catch(() => {});
  }, []);

  const set = (k) => (v) => setForm(prev => ({ ...prev, [k]: v }));
  const setEvt = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const sectionValid = (id) => {
    if (id === 'personal') {
      const nameOk = form.full_name.trim().length >= 2;
      const ssnOk = !!form.ssn_encrypted;
      const haveAtLeastOne = (form.attachment_ids || []).length >= 1;
      return nameOk && ssnOk && haveAtLeastOne;
    }
    if (id === 'contact') return /^\S+@\S+\.\S+$/.test(form.email) && form.phone.replace(/\D/g, '').length >= 7;
    if (id === 'work') return !!form.work_location;
    if (id === 'review') return form.consent_contact === true;
    return true;
  };

  const handleSubmit = async () => {
    if (!sectionValid('review')) {
      toast.error('Please confirm you consent to be contacted.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        date_of_birth: form.date_of_birth || null,
        earliest_start_date: form.earliest_start_date || null,
      };
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' && k !== 'full_name' && k !== 'email' && k !== 'phone') {
          payload[k] = null;
        }
      });
      await OnboardingPublicAPI.submit(payload);
      clearDraft();
      setDone(true);
    } catch (e) {
      toast.error(`Submission failed: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const total = SECTIONS.length;
  const section = SECTIONS[step];
  const completion = useMemo(() => calcCompletion(form), [form]);

  if (done) return <DoneScreen onBack={() => navigate('/login')} isLight={isLight}
    applicantName={form.preferred_name || form.full_name} />;

  return (
    <div className="min-h-screen relative overflow-x-hidden"
      style={{
        background: isLight
          ? 'radial-gradient(circle at 20% 0%, #FFE5E7 0%, #FFFAF0 30%, #FFFFFF 60%)'
          : 'radial-gradient(circle at 20% 0%, #1a0407 0%, #0A0A0A 40%)',
        color: isLight ? '#0f172a' : '#F5F5F5',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
      data-testid="onboarding-page">
      <WelcomeIntro />
      <CursorGlow isLight={isLight} />
      <FloatingShapes isLight={isLight} />

      <div className="relative z-10 max-w-3xl mx-auto px-4 py-6 sm:py-10">
        <div className="flex items-center justify-between mb-6 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <ConnectionSourceLogo size="sm" />
            <span className="hidden sm:inline text-[10px] font-black uppercase tracking-[0.22em]"
              style={{ color: isLight ? '#64748b' : '#A3A3A3' }}>
              · Onboarding
            </span>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <AutosaveChip form={form} />
            <CompletionRing percent={completion} />
            <button onClick={() => navigate('/login')}
              className="text-[11px] font-black inline-flex items-center gap-1 hover:underline"
              style={{ color: isLight ? '#64748b' : '#A3A3A3' }}
              data-testid="onboarding-back-to-login">
              ← Sign in
            </button>
          </div>
        </div>

        <ProgressIndicator total={total} current={step} isLight={isLight} sections={SECTIONS}
          applicantName={form.preferred_name || form.full_name}
          onJump={(i) => i <= step && setStep(i)} />

        <motion.div
          key={section.id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.2, 0.9, 0.3, 1] }}
          className="rounded-3xl overflow-hidden mt-5 shadow-2xl"
          style={{
            background: isLight ? 'rgba(255,255,255,0.85)' : 'rgba(20,20,28,0.72)',
            border: isLight ? '1px solid rgba(15,23,42,0.08)' : '1px solid rgba(255,255,255,0.06)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
          }}>
          <SectionHero section={section} applicantName={form.preferred_name || form.full_name} />

          <div className="p-5 sm:p-7">
            <AnimatePresence mode="wait">
              <motion.div key={section.id}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.25 }}>
                {section.id === 'personal' && <PersonalSection form={form} setEvt={setEvt} set={set} isLight={isLight} />}
                {section.id === 'contact'  && <ContactSection  form={form} setEvt={setEvt} isLight={isLight} />}
                {section.id === 'work'     && <WorkSection     form={form} setEvt={setEvt} set={set} locations={locations} isLight={isLight} />}
                {section.id === 'review'   && <ReviewSection   form={form} set={set} isLight={isLight} />}
              </motion.div>
            </AnimatePresence>
          </div>

          <NavigationBar step={step} total={total} isLight={isLight}
            canAdvance={sectionValid(section.id)}
            submitting={submitting}
            onBack={() => setStep(s => Math.max(0, s - 1))}
            onNext={() => setStep(s => Math.min(total - 1, s + 1))}
            onSubmit={handleSubmit}
          />
        </motion.div>
      </div>
    </div>
  );
}
